import { useMemo } from 'react';
import { canSee, useMyPermissions } from '../auth/useMyPermissions';
import { useCompanies, useLeads } from '../tracker/hooks';
import type { PickerEntity } from './OutboundPicker';

/**
 * The Outbound records Deals can link to, plus whether this person may see
 * them at all.
 *
 * Deals and Outbound are gated separately in the permissions matrix, so a
 * sales-team member can hold Deals and not Outbound. The queries are skipped
 * entirely in that case rather than fired and allowed to fail: a refused
 * request is not a useful way to discover a permission we already know about.
 */

interface OutboundEntities {
  companies: PickerEntity[];
  leads: PickerEntity[];
  canReachOutbound: boolean;
}

export function useOutboundEntities(): OutboundEntities {
  const permissions = useMyPermissions();
  const canReachOutbound = canSee(permissions.data, 'sales_tracker');

  const companiesQuery = useCompanies();
  const leadsQuery = useLeads();

  const companies = useMemo<PickerEntity[]>(() => {
    if (!canReachOutbound) return [];
    return (companiesQuery.data ?? [])
      .map((company) => ({ id: company.id, label: company.name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [canReachOutbound, companiesQuery.data]);

  const leads = useMemo<PickerEntity[]>(() => {
    if (!canReachOutbound) return [];
    return (leadsQuery.data ?? [])
      .map((lead) => ({ id: lead.id, label: leadLabel(lead) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [canReachOutbound, leadsQuery.data]);

  return { companies, leads, canReachOutbound };
}

/**
 * A lead is a person, and the useful handle is their name. Falling through to
 * the company and then the email means a row with a blank name still reads as
 * something rather than as an empty option nobody can pick on purpose.
 */
export function leadLabel(lead: {
  first_name: string;
  last_name: string;
  company_name: string;
  email: string;
}): string {
  const name = `${lead.first_name} ${lead.last_name}`.trim();
  const company = lead.company_name.trim();
  if (name !== '') {
    return company === '' ? name : `${name} — ${company}`;
  }
  if (company !== '') return company;
  return lead.email.trim() === '' ? 'Unnamed lead' : lead.email.trim();
}
